import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#FF6B35' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-medium mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div className="w-[8px] h-[8px] rounded-full bg-[#FF6B35]" />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const ChartingAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('week');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/analytics/charting?filter=${filter}`, token);
            setAnalyticsData(response.data.data.response);
        } catch (error) {
            console.error('Failed to load charting analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        return new Intl.NumberFormat('en-US').format(num);
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                    <p className="text-[12px]">Loading charting analytics...</p>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] h-screen bg-black text-white relative">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-[24px] h-[24px] rounded-md flex items-center justify-center text-[8px] font-medium text-white"
                                style={{ backgroundColor: '#FF6B35' }}
                            >
                                CH
                            </div>
                            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Charting Analytics</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className='text-[red] cursor-pointer text-[12px]'>Export data</p>
                            <div 
                                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                onClick={() => navigate('/analytics/hub')}
                            >
                                Back
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                        {/* Filter */}
                        <div className="mb-6">
                            <select 
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="bg-[#1A1A1A] border border-[#333] text-white text-[12px] px-3 py-2 rounded"
                            >
                                <option value="day">Last 24 Hours</option>
                                <option value="week">Last Week</option>
                                <option value="month">Last Month</option>
                                <option value="year">Last Year</option>
                                <option value="all">All Time</option>
                            </select>
                        </div>

                        {analyticsData ? (
                            <div className="space-y-4">
                                {/* Top Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Chart Entries"
                                        value={formatNumber(analyticsData.chartEntries || 0)}
                                        subtitle="New chart positions"
                                    />
                                    <MetricCard
                                        title="Peak Position"
                                        value={analyticsData.peakPosition || '--'}
                                        subtitle="Highest chart ranking"
                                    />
                                </div>

                                {/* Second Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Charts Active"
                                        value={formatNumber(analyticsData.activeCharts || 0)}
                                        subtitle="Currently charting"
                                    />
                                    <MetricCard
                                        title="Avg Position"
                                        value={analyticsData.avgPosition || '--'}
                                        subtitle="Average chart rank"
                                    />
                                </div>

                                {/* Chart Performance */}
                                <ContentCard title="Top Chart Performances">
                                    <div className="space-y-3">
                                        {analyticsData.topCharts && analyticsData.topCharts.length > 0 ? (
                                            analyticsData.topCharts.map((chart, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-light">{chart.songTitle}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">{chart.chartName}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#FF6B35] text-[12px] font-medium">#{chart.position}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">Peak</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No chart data available</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Chart Movement */}
                                <ContentCard title="Recent Chart Movement">
                                    <div className="space-y-3">
                                        {analyticsData.recentMovement && analyticsData.recentMovement.length > 0 ? (
                                            analyticsData.recentMovement.map((movement, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-light">{movement.songTitle}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">{movement.chartName}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className={`text-[12px] font-medium ${movement.direction === 'up' ? 'text-green-400' : movement.direction === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
                                                            {movement.direction === 'up' ? '↗' : movement.direction === 'down' ? '↘' : '→'} {movement.change}
                                                        </div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">vs last period</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No movement data available</p>
                                        )}
                                    </div>
                                </ContentCard>
                            </div>
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No charting data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default ChartingAnalytics; 